import React, { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { Divider, Grid, styled, Typography, IconButton, Box } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";

import ButtonBlue from "../../../../shared/general/ButtonBlue";

import { deleteEmployeePosition, getEmployeePositionChange, getEmployeeProfile } from "../../../../../../actions/employee";
import { getCompanyContract } from "../../../../../../actions/vendor";

import DrawerEdit from "./DrawerEdit";
import DialogConfirmDelete from "../../../../shared/general/DialogConfirmDelete";
import { openNotificationAlert } from "../../../../../../actions/notificationAlert";
import { getName } from "../../../../../../utils/userData";
import { getDayjsFormatWithTranslate } from "../../../../../../utils";

const StyledHeadLabel = styled(Typography)({
  fontWeight: 600,
  fontSize: 14,
});

const StyledWrapTop = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  marginBottom: 16,
  "& .MuiTypography-body2": {
    margin: 0,
  },
});

const StyledDivider = styled(Divider)({
  marginTop: 16,
  marginBottom: 16,
  borderWidth: "0px 0px thin",
  borderColor: "#919eab52",
  borderStyle: "dashed",
  width: "100%",
});

const StyledWrapChange = styled("div")({
  marginTop: 8,
  "& .inner": {
    marginBottom: 8,
  },
  "& .first-line": {
    display: "flex",
    "& .date": {
      fontWeight: 600,
    },
  },
  "& .positions-name": {
    fontWeight: 600,
    marginLeft: 8,
  },
  "& .second-line": {
    marginLeft: 64,
    fontSize: 16,
    "&.name-line, .name-line": {
      fontWeight: 500,
    },
  },
});

const Transfer = () => {
  const dispatch = useDispatch();
  const { result: employeeProfile } = useSelector(
    (state) => state.employeeProfile
  );
  const { result: employeePosition } = useSelector(
    (state) => state.employeePosition
  );
  const { t, i18n } = useTranslation();

  const [drawerConfig, setDrawerConfig] = useState({
    isOpen: false,
    data: {},
  });

  const [dialogDeleteConfig, setDialogDeleteConfig] = useState({
    isOpen: false,
    data: {},
  })

  const handleOpenDrawerAdd = () => {
    setDrawerConfig(prev => ({
      ...prev,
      isEdit: false,
      isOpen: true,
      data: {},
    }))
  }

  const handleOpenDrawerEdit = (data) => {
    setDrawerConfig(prev => ({
      ...prev,
      isEdit: true,
      isOpen: true,
      data: data,
    }))
  }

  const handleCloseDrawerEdit = (data) => {
    setDrawerConfig(prev => ({
      ...prev,
      isOpen: false,
    }))
  }

  const handleOpenDialogDeleteConfig = (data) => {
    setDialogDeleteConfig(prev => ({
      ...prev,
      isOpen: true,
      data: data,
    }))
  }

  const handleCloseDialogDeleteConfig = () => {
    setDialogDeleteConfig(prev => ({
      ...prev,
      isOpen: false,
    }))
  }

  const handleDeleteEmployeePosition = async () => {

    const result = await dispatch(deleteEmployeePosition(dialogDeleteConfig.data.idEmployeePosition));

    if(result && result.status === 200) {
      handleCloseDialogDeleteConfig();
      dispatch(openNotificationAlert({
        type: "success",
        message: t("DataSaveSuccessful"),
      }));
      dispatch(getEmployeeProfile(employeeProfile.idEmp));
      dispatch(getEmployeePositionChange(employeeProfile.idEmp));
      
    } else {
      dispatch(openNotificationAlert({
        type: "error",
        message: t("AnErrorOccurred")
      }));
    }

  }
  
  useEffect(() => {
    if (employeeProfile) {
      dispatch(getEmployeePositionChange(employeeProfile.idEmp));
      dispatch(getCompanyContract());
    }
  }, [employeeProfile]);

  return (
    <div>
      <StyledWrapTop>
        <div>
          <ButtonBlue
            size="small"
            startIcon={<EditIcon />}
            onClick={() => {handleOpenDrawerAdd()}}
          >
            {t("Edit")}
          </ButtonBlue>
        </div>
      </StyledWrapTop>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div>
            <StyledHeadLabel color="text.secondary">
              {t("Company")}
            </StyledHeadLabel>
            <Typography variant="h6">
              {employeeProfile.companyName ? employeeProfile.companyName : "-"}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div>
            <StyledHeadLabel color="text.secondary">
              {t("BusinessUnit")}
            </StyledHeadLabel>
            <Typography variant="h6">
              {employeeProfile.businessUnitName ? employeeProfile.businessUnitName : "-"}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div>
            <StyledHeadLabel color="text.secondary">
              {t("Division")}
            </StyledHeadLabel>
            <Typography variant="h6">
              {employeeProfile.divisionName
                ? employeeProfile.divisionName
                : "-"}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div>
            <StyledHeadLabel color="text.secondary">
              {t("Department")}
            </StyledHeadLabel>
            <Typography variant="h6">
              {employeeProfile.departmentName
                ? employeeProfile.departmentName
                : "-"}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div>
            <StyledHeadLabel color="text.secondary">
              {t("Section")}
            </StyledHeadLabel>
            <Typography variant="h6">
              {employeeProfile.sectionName ? employeeProfile.sectionName : "-"}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div>
            <StyledHeadLabel color="text.secondary">
              {t("Position")}
            </StyledHeadLabel>
            <Typography variant="h6">
              {employeeProfile.positionsName
                ? employeeProfile.positionsName
                : "-"}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div>
            <StyledHeadLabel color="text.secondary">
              {t("JobGroup")}
            </StyledHeadLabel>
            <Typography variant="h6">
              {employeeProfile.jobGroupName
                ? employeeProfile.jobGroupName
                : "-"}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div>
            <StyledHeadLabel color="text.secondary">
              {t("JobLevel")}
            </StyledHeadLabel>
            <Typography variant="h6">
              {employeeProfile.jobLevelName
                ? employeeProfile.jobLevelName
                : "-"}
            </Typography>
          </div>
        </Grid>
      </Grid>
      <StyledDivider />
      <div>
        <StyledHeadLabel color="text.secondary">
          {t("ChangeLog")}
        </StyledHeadLabel>

        {employeePosition && (
          <StyledWrapChange>
            {employeePosition.map((item, index) => (
              <div className="inner" key={index}>
                <div className="first-line" style={{ marginBottom: "16px" }}>
                  {index === 0 && <Box display="flex" gap="4px">
                    {<IconButton
                      aria-label="delete"
                      size="small"
                      onClick={() => handleOpenDialogDeleteConfig(item)}
                      disabled={employeePosition.length === 1}
                    >
                      <HighlightOffRoundedIcon fontSize="inherit" />
                    </IconButton>}
                    <IconButton
                      aria-label="edit"
                      size="small"
                      onClick={() => handleOpenDrawerEdit(item)}
                    >
                      <EditNoteRoundedIcon fontSize="inherit" />
                    </IconButton>
                  </Box>}
                  &nbsp;
                  {item.end ? (
                    <Typography className="date" color="text.third">
                      {`[${getDayjsFormatWithTranslate(item.start, "DD MMM YYYY", i18n.language)} - ${getDayjsFormatWithTranslate(item.end, "DD MMM YYYY", i18n.language)}]`}
                    </Typography>
                  ) : (
                    <Typography className="date" color="text.third">
                      {`[${getDayjsFormatWithTranslate(item.start, "DD MMM YYYY", i18n.language)} - ${t("Current")}]`}
                    </Typography>
                  )}
                  <Typography className="positions-name">
                    {`${item.positionsName}`}
                  </Typography>
                </div>
                
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t("Company")}: {getName({name: item.companyName, name_EN: item.companyName_EN})}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t("BusinessUnit")}: {getName({name: item.businessUnitName, name_EN: item.businessUnitName_EN})}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t("Division")}: {getName({name: item.divisionName, name_EN: item.divisionName_EN})}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t("Department")}: {getName({name: item.departmentName, name_EN: item.departmentName_EN})}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t("Section")}: {getName({name: item.sectionName, name_EN: item.sectionName_EN})}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {`${t("ManagerLV")} 1`}
                      </Typography>
                      <Typography variant="body2" color="text.third">
                        {item.managerLV1Name? `${item.managerLV1Name}`: "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.managerLV1Email? `(${item.managerLV1Email})`: ""}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {`${t("ManagerLV")} 2`}
                      </Typography>
                      <Typography variant="body2" color="text.third">
                        {item.managerLV2Name? `${item.managerLV2Name}`: "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.managerLV2Email? `(${item.managerLV2Email})`: ""}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {`${t("Description")}: ${item.remark || "-"}`}
                    </Typography>
                  </Grid>

                  {/* <Grid item xs={12}>
                    <Box mt={1}>
                      <Typography variant="caption" color="text.disabled" display="block">
                        {`${t("UpdateDate")}: ${item.updateDate? getDayjsFormatWithTranslate(item.updateDate, "D MMM YYYY HH:mm", i18n.language): "-"}`}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" display="block">
                        {`${t("UpdateBy")}: ${item.updater? item.updater: "-"}`}
                      </Typography>
                    </Box>
                  </Grid> */}

                </Grid>

                <Typography
                  color="text.secondary"
                  variant="body2"
                  className="second-line"
                  align="right"
                  style={{ fontStyle: "oblique", fontSize: 14 }}
                >
                  {`วันที่แก้ไขข้อมูล: ${
                    item.updateDate
                      ? `${dayjs(item.updateDate)
                          .locale(localStorage.getItem("language") || "th")
                          .format(
                            localStorage.getItem("language") === "th"
                              ? "DD MMM BBBB HH:mm"
                              : "DD MMM YYYY HH:mm"
                          )}`
                      : "-"
                  }`}
                </Typography>
                <Typography
                  color="text.secondary"
                  variant="body2"
                  className="second-line"
                  align="right"
                  style={{ fontStyle: "oblique", fontSize: 14 }}
                  gutterBottom
                >
                  {`${
                    item.adminVendor_firstname
                      ? ` โดย ${item.adminVendor_firstname} ${item.adminVendor_lastname}`
                      : item.admin_firstname
                      ? ` โดย ${item.admin_firstname} ${item.admin_lastname}`
                      : ""
                  }`}
                </Typography>
              </div>
            ))}
          </StyledWrapChange>
        )}
      </div>
      <DrawerEdit
        drawerConfig={drawerConfig}
        handleClose={handleCloseDrawerEdit}
      />

      <DialogConfirmDelete
        open={dialogDeleteConfig.isOpen}
        handleClose={handleCloseDialogDeleteConfig}
        text1={`${t("Position")}`}
        text={`${dialogDeleteConfig.data.positionsName}`}
        handleDelete={handleDeleteEmployeePosition}
      />
    </div>
  );
};

export default Transfer;
